import { useState } from "react";
import { Link } from "react-router-dom";
import { useSessions, useSessionExercises, SessionHistoryRow, SessionExerciseRow } from "../lib/queries";
import { LoadingState, ErrorState } from "./Loading";
import { formatSeconds } from "../lib/planElements";
import { CheckinTags } from "./CheckinTags";

/**
 * A client's training history.
 *
 * This is the read side of session logging, which did not exist before: sessions were written and never
 * displayed anywhere, so a coach could not tell whether an athlete had trained at all -- and, more
 * seriously, an athlete flagging pain or an inability to finish an exercise produced no visible signal
 * to anyone. Flagged sessions are therefore the thing this leads with, not a detail hidden behind a
 * click.
 */
export function SessionHistory({
  clientId,
  /**
   * Exercise names per plan id, when the parent already has them.
   *
   * The session log stores an element *index*, not a name -- an exercise renamed later must not rewrite
   * history. The coach's plan builder already holds every plan, so it can supply the names for free;
   * without them the detail still renders, just as "Exercise 2" rather than "Box Jumps".
   */
  elementNamesByPlan,
  /**
   * Where a row's "Full details" link points. Coach and family reach the same page by different routes
   * -- the family one has no coach chrome and no way back into a coach screen.
   */
  detailBase = "/coach/sessions",
}: {
  clientId: string;
  elementNamesByPlan?: Record<string, string[]>;
  detailBase?: string;
}) {
  const sessionsQuery = useSessions(clientId);

  return (
    <section>
      <h2>Training history</h2>

      {sessionsQuery.isPending && <LoadingState label="Loading training history…" />}
      {sessionsQuery.isError && <ErrorState error={sessionsQuery.error} onRetry={() => sessionsQuery.refetch()} />}

      {sessionsQuery.isSuccess &&
        (sessionsQuery.data.length === 0 ? (
          <p className="empty-state">No sessions logged yet.</p>
        ) : (
          <ul className="client-list">
            {sessionsQuery.data.map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                elementNames={elementNamesByPlan?.[session.plan_id]}
                detailBase={detailBase}
              />
            ))}
          </ul>
        ))}
    </section>
  );
}

/**
 * Renders the date in the viewer's own locale and timezone. `completed_at` is a UTC instant from the
 * Lambda clock, so a session logged late in the evening would otherwise display as the next day for
 * anyone west of UTC.
 */
function formatCompletedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

/** "8 reps · 35 · RPE 7", or "30s · RPE 6" for a hold. Null when the athlete recorded nothing at all. */
function describeSet(exercise: SessionExerciseRow): string | null {
  if (exercise.status === "skipped") return "skipped";
  const parts = [
    exercise.reps !== null ? `${exercise.reps} reps` : null,
    exercise.duration_seconds !== null ? formatSeconds(exercise.duration_seconds) : null,
    exercise.weight !== null ? `${exercise.weight}` : null,
    exercise.rpe !== null ? `RPE ${exercise.rpe}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * One session's sets, grouped by exercise.
 *
 * Grouped rather than listed flat because the question a coach is asking is per-exercise: not "what
 * happened" but "did this hold up across its sets". Three rows under one heading answer that at a
 * glance; twelve flat rows do not.
 */
function SessionDetail({ sessionId, elementNames }: { sessionId: string; elementNames?: string[] }) {
  const exercisesQuery = useSessionExercises(sessionId, true);

  if (exercisesQuery.isPending) return <LoadingState label="Loading sets…" />;
  if (exercisesQuery.isError) {
    return <ErrorState error={exercisesQuery.error} onRetry={() => exercisesQuery.refetch()} />;
  }

  const byExercise = new Map<number, SessionExerciseRow[]>();
  for (const exercise of exercisesQuery.data) {
    byExercise.set(exercise.element_index, [...(byExercise.get(exercise.element_index) ?? []), exercise]);
  }
  if (byExercise.size === 0) return <p className="plan-summary">No sets recorded.</p>;

  return (
    <div className="session-detail">
      {[...byExercise.entries()]
        .sort(([a], [b]) => a - b)
        .map(([elementIndex, sets]) => (
          <div key={elementIndex} className="session-exercise">
            <p className="element-name">{elementNames?.[elementIndex] ?? `Exercise ${elementIndex + 1}`}</p>
            <ol className="element-list">
              {[...sets]
                .sort((a, b) => a.set_index - b.set_index)
                .map((set) => {
                  const summary = describeSet(set);
                  return (
                    <li key={set.id} className="element-row">
                      <span className="element-tag">Set {set.set_index + 1}</span>
                      <span className={set.status === "skipped" ? "plan-summary" : "element-prescription"}>
                        {summary ?? "no detail recorded"}
                      </span>
                      {set.flagged && <span className="badge badge-danger">Flagged</span>}
                      {set.flag_note && <span className="element-notes">{String(set.flag_note)}</span>}
                    </li>
                  );
                })}
            </ol>
          </div>
        ))}
    </div>
  );
}

function SessionRow({
  session,
  elementNames,
  detailBase,
}: {
  session: SessionHistoryRow;
  elementNames?: string[];
  detailBase: string;
}) {
  const [expanded, setExpanded] = useState(false);
  // Defaulted rather than trusted. The server always sends this, but a render that assumes a field is
  // present throws inside React and takes down the whole page -- this component is mounted alongside the
  // plan builder, so one unexpected response shape would blank the coach's plans too.
  const flagged = session.flagged_exercises ?? [];

  return (
    <li className={session.issue_flagged ? "client-card session-card session-flagged" : "client-card session-card"}>
      <div className="session-card-main">
        <span className="client-name">{formatCompletedAt(session.completed_at)}</span>
        {session.issue_flagged ? (
          <span className="badge badge-danger">Issue flagged</span>
        ) : (
          <span className="badge badge-ok">Completed</span>
        )}
        {/* Who actually submitted it: a guardian logging for a minor is a different signal from the
            athlete logging for themselves, and the distinction is already recorded server-side. */}
        <span className="badge">{session.logged_by_role === "guardian" ? "Logged by guardian" : "Logged by athlete"}</span>
        {/* The RPE, on the row.
            The history card carried a date, "Completed", and who logged it -- nothing comparable at a
            glance, so a list of them was not a trend, and the rating the athlete was carefully asked
            for at the end of every session appeared nowhere until you opened the record (UX review
            F79). This is the one number that makes two sessions comparable without opening either. */}
        {session.session_rpe !== null && session.session_rpe !== undefined && (
          <span className="badge" title="How hard the athlete said it felt, 1-10">
            RPE {session.session_rpe}
          </span>
        )}
        <button type="button" className="chip" onClick={() => setExpanded((open) => !open)}>
          {expanded ? "Hide sets" : "View sets"}
        </button>
        {/* The inline toggle answers "what did they do"; the page answers "against what" -- it carries
            the prescription each set was aiming at, which no amount of expanding here can show. */}
        <Link className="chip" to={`${detailBase}/${session.id}`}>
          Full details
        </Link>
      </div>

      <CheckinTags checkin={session.checkin} />

      {flagged.length > 0 && (
        <ul className="exercise-list">
          {flagged.map((exercise) => (
            <li key={exercise.id}>
              {elementNames?.[exercise.element_index] ?? `Exercise ${exercise.element_index + 1}`}
              {exercise.status === "skipped" ? " (skipped)" : ""}
              {exercise.flag_note ? ` — ${exercise.flag_note}` : ""}
            </li>
          ))}
        </ul>
      )}

      {expanded && <SessionDetail sessionId={session.id} elementNames={elementNames} />}
    </li>
  );
}
